import * as React from 'react';
import styled, { css } from 'styled-components';
import { rem } from 'polished';
import { ButtonStyledStyles, ButtonStyles } from '../_helpers/form/Button';
import { __ } from '@bart.sk-eshop/react-i18n';
import { ArrowRightIcon } from '../_layout/SideMenu/icons';

const ButtonStyledStylesPagination = css`
  ${ButtonStyledStyles};
  &.inFilter:not(.primary) {
    background: ${({ theme }) => theme.color.white};
    border-color: #d6d6d6;
    box-shadow: 0 ${rem(2)} ${rem(4)} rgba(0, 0, 0, 0.05);
    color: #171717;
  }
`;

const PaginationInFilterItem = css`
  &.inFilter {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    width: ${rem(36)};
    height: ${rem(36)};
    padding: 0;
    margin: 0 ${rem(4)};
    font-size: ${rem(15)};
    border-radius: ${rem(4)};
    vertical-align: middle;
  }
`;

const PaginationWrapper = styled.div`
  background-color: ${({ theme }) => theme.color.background};
  font-size: ${rem(14)};
  line-height: 1;
  text-align: center;
  padding: ${rem(20)} 0;
  margin-right: ${({ theme }) => rem(-theme.grid.padding / 2)};
  margin-left: ${({ theme }) => rem(-theme.grid.padding / 2)};
  text-wrap: nowrap;

  ${({ theme }) => theme.media('sm')} {
    margin-right: ${({ theme }) => rem(-theme.grid.padding / 4)};
    margin-left: ${({ theme }) => rem(-theme.grid.padding / 4)};
    background-color: initial;
  }
  &.no-margin {
    display: inline-block;
    padding: ${rem(6)} 0;
    text-align: center;
    float: right;
    ${({ theme }) => theme.media('sm')} {
      padding-right: ${rem(12)};
    }
  }
`;

const PaginationItemStyles = css`
  display: inline-block;
  border-radius: ${rem(4)};
  text-align: center;
  padding: ${rem(13)} ${rem(5)};
  margin-right: ${rem(2)};
  margin-left: ${rem(2)};
`;

const PaginationItem = styled.button`
  ${ButtonStyles};
  ${ButtonStyledStylesPagination};
  background-color: transparent;
  width: ${rem(40)};
  padding-right: ${rem(5)};
  padding-left: ${rem(5)};
  margin-right: ${rem(2)};
  margin-left: ${rem(2)};
  ${PaginationInFilterItem};
`;

const PaginationItemActive = styled.span`
  ${ButtonStyles};
  ${ButtonStyledStylesPagination};
  width: ${rem(40)};
  padding-right: ${rem(5)};
  padding-left: ${rem(5)};
  margin-right: ${rem(2)};
  margin-left: ${rem(2)};
  ${PaginationInFilterItem};
`;

const PaginationSpacer = styled.span`
  ${PaginationItemStyles};
  border-color: transparent;
  //width: ${rem(40)};
  margin-right: ${rem(2)};
  margin-left: ${rem(2)};
  ${PaginationInFilterItem};
  /* figma: the ellipsis is a page-button look-alike, but not interactive. */
  &.inFilter {
    background: ${({ theme }) => theme.color.white};
    border: ${rem(1)} solid #d6d6d6;
    box-shadow: 0 ${rem(2)} ${rem(4)} rgba(0, 0, 0, 0.05);
    color: #171717;
    cursor: default;
  }
`;

const PaginationItemArrow = styled(PaginationItem)`
  &:not(.inFilter) {
    background-color: transparent;
    border-color: transparent;
  }
  &:disabled {
    opacity: 0.2;
  }
  /* figma in-filter arrows are bordered white buttons; dim when disabled. */
  &.inFilter:disabled {
    opacity: 0.4;
  }
`;

const PaginationNext = styled(ArrowRightIcon)`
  /* Generic pagination (e.g. below the product grid): 16px icon. */
  width: ${rem(16)};
  height: ${rem(16)};
  vertical-align: middle;

  /* figma page-button (the in-filter pagination): 20px icon in the 36px
     bordered button. */
  .inFilter > & {
    width: ${rem(20)};
    height: ${rem(20)};
  }
`;

const PaginationPrev = styled(PaginationNext)`
  transform: rotate(180deg);
`;

interface PaginationIO {
  total: number;
  current: number | string;
  onPageChanged: (pageNumner: number) => void;
  inFilter?: boolean;
  filled?: boolean;
}

class Pagination extends React.Component<PaginationIO> {
  public render() {
    let { current } = this.props;
    const { total, inFilter } = this.props;
    if (typeof current === 'string') {
      current = parseInt(current, 10);
    }
    const paginationNumbers = this.pagination(current, total);

    return (
      <PaginationWrapper className={inFilter ? 'no-margin' : ''}>
        <PaginationItemArrow
          onClick={this.props.onPageChanged.bind(this, current - 1)}
          disabled={current === 1}
          className={inFilter ? 'inFilter' : ''}
          filled={this.props.filled}
          aria-label={__('Predchadzajúca stránka')}
        >
          <PaginationPrev />
        </PaginationItemArrow>

        {paginationNumbers &&
          paginationNumbers.map((oneNumber, i) => {
            if (Number.isInteger(oneNumber)) {
              if (oneNumber === current) {
                return (
                  <PaginationItemActive
                    className={inFilter ? 'inFilter primary' : 'primary'}
                    onClick={this.props.onPageChanged.bind(this, oneNumber)}
                    key={i}
                  >
                    {oneNumber}
                  </PaginationItemActive>
                );
              } else {
                return (
                  <PaginationItem
                    className={inFilter ? 'inFilter' : ''}
                    filled={this.props.filled}
                    onClick={this.props.onPageChanged.bind(this, oneNumber)}
                    key={i}
                  >
                    {oneNumber}
                  </PaginationItem>
                );
              }
            } else {
              return (
                <PaginationSpacer
                  className={inFilter ? 'inFilter' : ''}
                  key={i}
                >
                  &hellip;
                </PaginationSpacer>
              );
            }
          })}

        <PaginationItemArrow
          className={inFilter ? 'inFilter' : ''}
          onClick={this.props.onPageChanged.bind(this, current + 1)}
          disabled={current === total}
          filled={this.props.filled}
          aria-label={__('Ďalšia stránka')}
        >
          <PaginationNext />
        </PaginationItemArrow>
      </PaginationWrapper>
    );
  }

  public pagination(currentPage, pageCount) {
    const delta = 1;
    const left = currentPage - delta;
    const right = currentPage + delta + 1;
    let result: any = [];

    result = Array.from({ length: pageCount }, (v, k) => k + 1).filter(
      (i) => i && i >= left && i < right,
    );

    if (result.length > 1) {
      // Add first page and dots
      if (result[0] > 1) {
        if (result[0] > 2) {
          result.unshift('...');
        }
        result.unshift(1);
      }

      // Add dots and last page
      if (result[result.length - 1] < pageCount) {
        if (result[result.length - 1] !== pageCount - 1) {
          result.push('...');
        }
        result.push(pageCount);
      }
    }

    return result;
  }
}

export default Pagination;
